import React, { useState, useEffect } from 'react';
import { Ticket, Shield, Scale } from 'lucide-react';
import NewsBar from '../components/news/NewsBar';
import BotCard from '../components/dashboard/BotCard';
import RedeemCouponModal from '../components/dashboard/RedeemCouponModal';
import SubBotModal from '../components/dashboard/SubBotModal';
import ConfidentialityModal from '../components/dashboard/ConfidentialityModal';
import TermsOfUseModal from '../components/dashboard/TermsOfUseModal';
import { bots } from '../data/bots';
import { useAuth } from '../contexts/AuthContext';
import { cleanupMindStudio } from '../utils/mindstudio';
import { useNavigate } from 'react-router-dom';
import type { Bot, SubBot } from '../types';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedBot, setSelectedBot] = useState<Bot | null>(null);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showSubBotModal, setShowSubBotModal] = useState(false);
  const [showConfidentialityModal, setShowConfidentialityModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    const lastBotId = sessionStorage.getItem('lastBotId');
    if (lastBotId) {
      sessionStorage.removeItem('lastBotId');
      cleanupMindStudio();
    }

    // Listen for the custom event to open the Terms modal
    const handleOpenTermsModal = () => {
      setShowTermsModal(true);
    };

    window.addEventListener('open-terms-modal', handleOpenTermsModal);

    return () => {
      window.removeEventListener('open-terms-modal', handleOpenTermsModal);
    };
  }, []);

  const handleBotClick = (bot: Bot) => {
    setSelectedBot(bot);
    
    // Special case for PromptVersus
    if (bot.id === 'other-bots' && bot.subBots?.some(sb => sb.id === '95b30ce0-8108-44ff-bcad-31b71c9f2fcd')) {
      const promptVersusBot = bot.subBots?.find(sb => sb.id === '95b30ce0-8108-44ff-bcad-31b71c9f2fcd');
      if (promptVersusBot) {
        navigate('/promptversus');
        return;
      }
    }
    
    if (bot.subBots) {
      setShowSubBotModal(true);
    } else {
      sessionStorage.setItem('lastBotId', bot.id);
      window.location.href = `/bot/${bot.id}`;
    }
  };

  const handleSubBotSelect = (subBot: SubBot) => {
    setShowSubBotModal(false);
    
    // Special case for PromptVersus
    if (subBot.id === '95b30ce0-8108-44ff-bcad-31b71c9f2fcd') {
      navigate('/promptversus');
      return;
    }
    
    sessionStorage.setItem('lastBotId', subBot.id);
    window.location.href = `/bot/${subBot.id}`;
  };

  if (!user) return null;

  const financeBots = bots.filter(bot => bot.category === 'finance');
  const otherBots = bots.filter(bot => bot.category === 'other');

  return (
    <>
      <NewsBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Welcome and Action Buttons */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
            Welcome, {user.name}!
          </h1>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <button
              onClick={() => setShowConfidentialityModal(true)}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors w-full sm:w-auto"
            >
              <Shield className="h-5 w-5" />
              <span>Confidentiality Assurance</span>
            </button>
            
            <button
              onClick={() => setShowTermsModal(true)}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors w-full sm:w-auto"
            >
              <Scale className="h-5 w-5" />
              <span>Terms of Use</span>
            </button>
            
            <button
              onClick={() => setShowCouponModal(true)}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors w-full sm:w-auto"
            >
              <Ticket className="h-5 w-5" />
              <span>Redeem Coupon</span>
            </button>
          </div>
        </div>

        {/* Finance Bots Section */}
        <div className="mb-8 sm:mb-12">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
            Finance and Audit Bots
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {financeBots.map((bot) => (
              <BotCard
                key={bot.id}
                bot={bot}
                onClick={() => handleBotClick(bot)}
              />
            ))}
          </div>
        </div>

        {/* Other Bots Section */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
            Other Tools
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {otherBots.map((bot) => (
              <BotCard
                key={bot.id}
                bot={bot}
                onClick={() => handleBotClick(bot)}
              />
            ))}
          </div>
        </div>
      </div>

      {selectedBot?.subBots && (
        <SubBotModal
          isOpen={showSubBotModal}
          onClose={() => setShowSubBotModal(false)}
          subBots={selectedBot.subBots}
          onSelectBot={handleSubBotSelect}
        />
      )}
      
      <RedeemCouponModal
        isOpen={showCouponModal}
        onClose={() => setShowCouponModal(false)}
      />

      <ConfidentialityModal
        isOpen={showConfidentialityModal}
        onClose={() => setShowConfidentialityModal(false)}
      />

      <TermsOfUseModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </>
  );
};

export default Dashboard;